import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Single horizontal row always. Marquee + clone when row width > host; else centered row.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class MarqueeSlider extends Component {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {HTMLElement | null} */
  #clonedSlides = null;
  /**
   * Last host width the marquee was built for. iOS Safari fires resize /
   * ResizeObserver on every scroll (address bar show/hide changes the
   * viewport height), which would otherwise tear down and rebuild the
   * marquee — restarting the CSS animation from 0%. We only rebuild when
   * the width actually changes.
   * @type {number}
   */
  #lastWidth = -1;

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver = new ResizeObserver(this.#onHostResize);
    this.#resizeObserver.observe(this);
    window.addEventListener('resize', this.#onWindowResize);

    this.#initializeMarquee();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    window.removeEventListener('resize', this.#onWindowResize);
  }

  /** Rebuild only when the host width changed; ignore height-only resizes. */
  #onResize() {
    if (this.clientWidth === this.#lastWidth) return;
    this.#setupMarquee();
  }

  #onHostResize = debounce(() => {
    this.#onResize();
  }, 100);

  #onWindowResize = debounce(() => {
    this.#onResize();
  }, 150);

  #initializeMarquee() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#setupMarquee();
        requestAnimationFrame(() => {
          this.classList.remove('marquee-slider--loading');
        });
      });
    });
  }

  /**
   * Single-row width (slides + flex gap) vs host — not scrollWidth (wrap broke that before).
   * @param {HTMLElement} slidesContainer
   */
  #rowWiderThanHost(slidesContainer) {
    const hostW = this.clientWidth;
    if (hostW <= 0) return false;

    void slidesContainer.offsetWidth;

    const slides = slidesContainer.querySelectorAll(':scope > .marquee-slider__slide');
    if (slides.length === 0) return false;

    const gapRaw = getComputedStyle(slidesContainer).columnGap || getComputedStyle(slidesContainer).gap;
    const gap = parseFloat(gapRaw) || 0;

    let rowWidth = 0;
    slides.forEach((slide, i) => {
      rowWidth += slide.getBoundingClientRect().width;
      if (i < slides.length - 1) rowWidth += gap;
    });

    return Math.ceil(rowWidth) > Math.floor(hostW) + 1;
  }

  #setupMarquee() {
    const marqueeWrapper = /** @type {HTMLElement} */ (this.querySelector('.marquee-slider__marquee-wrapper'));
    const slidesContainer = /** @type {HTMLElement} */ (this.querySelector('.marquee-slider__slides'));
    if (!marqueeWrapper || !slidesContainer) return;

    this.#removeClonedSlides();
    this.classList.remove('marquee-slider--marquee');

    this.#lastWidth = this.clientWidth;

    const needsMarquee = this.#rowWiderThanHost(slidesContainer);

    if (needsMarquee) {
      this.#enableMarquee(marqueeWrapper, slidesContainer);
    } else {
      this.#disableMarquee();
    }
  }

  /**
   * @param {HTMLElement} marqueeWrapper
   * @param {HTMLElement} slidesContainer
   */
  #enableMarquee(marqueeWrapper, slidesContainer) {
    this.#clonedSlides = /** @type {HTMLElement} */ (slidesContainer.cloneNode(true));
    this.#clonedSlides.setAttribute('aria-hidden', 'true');
    this.#clonedSlides.classList.add('marquee-slider__slides--cloned');

    marqueeWrapper.appendChild(this.#clonedSlides);

    this.classList.add('marquee-slider--marquee');
    this.#setMarqueeSpeed(slidesContainer);
  }

  #disableMarquee() {
    this.#removeClonedSlides();
    this.classList.remove('marquee-slider--marquee');
  }

  #removeClonedSlides() {
    if (this.#clonedSlides) {
      this.#clonedSlides.remove();
      this.#clonedSlides = null;
    }
  }

  /**
   * @param {HTMLElement} slidesContainer
   */
  #setMarqueeSpeed(slidesContainer) {
    requestAnimationFrame(() => {
      const containerWidth = slidesContainer.offsetWidth;

      const computedStyle = getComputedStyle(this);
      const gapValue = computedStyle.getPropertyValue('--marquee-gap').trim();
      const gap = parseInt(gapValue, 10) || 25;

      const speed = Math.max(containerWidth / 20, 15);

      this.style.setProperty('--marquee-distance', `${containerWidth + gap}px`);
      this.style.setProperty('--marquee-slider-speed', `${speed}s`);
    });
  }

  /** Re-measure and restart marquee after the host becomes visible (e.g. cart drawer open). */
  reinitialize() {
    // Clear any paused play-state left by pauseMarquee() on the previous close,
    // otherwise the animation stays paused after reopening the drawer.
    const marqueeWrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('.marquee-slider__marquee-wrapper')
    );
    if (marqueeWrapper) {
      marqueeWrapper.style.animationPlayState = '';
    }

    this.#initializeMarquee();
  }

  /** Pause marquee animation while hidden (e.g. cart drawer close). */
  pauseMarquee() {
    const marqueeWrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('.marquee-slider__marquee-wrapper')
    );
    if (marqueeWrapper) {
      marqueeWrapper.style.animationPlayState = 'paused';
    }
  }
}

if (!customElements.get('marquee-slider-component')) {
  customElements.define('marquee-slider-component', MarqueeSlider);
}
